import { DEFAULT_CURRENCY, currencySymbol } from '../shared/currency'
import { CLIENT_BRAND_KEYS, MAIL_PLACEHOLDER_DOCUMENT_URL, MAIL_PLACEHOLDER_DOCUMENT_URL_LIST, MAIL_TEXT_PLACEHOLDERS } from '../shared/constants'
import { ISO_COUNTRY_CODES } from '../shared/countries'
import {
    fill, textOf, LANGUAGE, CLIENT_BRAND_NAMES, LABEL_PRESET_NAMES, LABEL_PRESET_OPTION, ENVELOPE_NAMES, ENVELOPE_OPTION, INVOICE_STATUS_LABELS, INVOICE_TAB_NAME, SUBSCRIPTION_STATUS_LABELS, DOC_CATEGORY_LABELS,
    SERVICES_MAIL_TEXTS_TEXTS, PAGES_INVOICE_TEMPLATE_EDITOR_TEXTS,
} from '../shared/texts'

// Lookups and formatters of the client — what used to sit in client/constants.ts beside the texts
// and is not a text: the brand list, the country lists, money formatting, the month-root folder key.
// The texts went to shared/texts.

export const invoiceStatusLabel = (key: string) => textOf(INVOICE_STATUS_LABELS, key)
export const subscriptionStatusLabel = (key: string) => textOf(SUBSCRIPTION_STATUS_LABELS, key)
export const docCategoryLabel = (key: string) => textOf(DOC_CATEGORY_LABELS, key)

// The tab name of an invoice form — ONE source for every opener and for the form itself, which
// renames its tab when the number arrives (finalize).
export const invoiceTabName = (inv: { invoiceNumber?: string }) =>
    inv.invoiceNumber ? fill(INVOICE_TAB_NAME.numbered, { invoiceNumber: inv.invoiceNumber }) : INVOICE_TAB_NAME.draft

// The hint to the document-link placeholder, appended to every placeholder list of the mail texts.
// Its name stands only in constants.ts.
export const DOCUMENT_URL_TIP = fill(SERVICES_MAIL_TEXTS_TEXTS.documentUrlTip, { placeholder: `{${MAIL_PLACEHOLDER_DOCUMENT_URL}}` })
    + ' ' + fill(SERVICES_MAIL_TEXTS_TEXTS.documentUrlListTip, { placeholder: `{${MAIL_PLACEHOLDER_DOCUMENT_URL_LIST}}` })

// Saving mail texts: every {name} that is no placeholder (operator, 27.10.2026, „144a") — also the
// encoded form a link editor stores in an address (%7Bname%7D). Case counts, as in the sending. It
// WARNS after saving, it does not block: a brace that is meant may stand in a text, and blocking would
// hold back the frame and the other fields saved with it. Empty = nothing to report.
const KNOWN_PLACEHOLDERS = new Set<string>(MAIL_TEXT_PLACEHOLDERS)
export function unknownPlaceholderWarning(...texts: (string | undefined)[]): string {
    const names = new Set<string>()
    for (const t of texts) {
        for (const m of (t ?? '').matchAll(/\{(\w+)\}|%7B(\w+)%7D/gi)) {
            const name = m[1] ?? m[2]
            if (!KNOWN_PLACEHOLDERS.has(name)) names.add(`{${name}}`)
        }
    }
    return names.size ? fill(SERVICES_MAIL_TEXTS_TEXTS.unknownPlaceholders, { names: [...names].join(', ') }) : ''
}

// A template version folder ('version-2') as people read it ('2') — the list and the editor's notice.
export const templateVersionNumber = (version: string) => version.replace(/^version-/, '')

// The PDF template editor: opened on a version that is NOT the one new documents are printed with,
// it says so (operator, 28.10.2026, „139"). `active` comes from the server's activeVersion() via the
// template list — the same source the list's badge reads. Empty = the active one, or nothing known.
export function inactiveVersionNotice(templates: { brand: string, version: string, active: boolean }[], brand: string, version: string): string {
    const active = templates.find((t) => t.brand === brand && t.active)
    if (!active || active.version === version) return ''
    return fill(PAGES_INVOICE_TEMPLATE_EDITOR_TEXTS.inactiveVersion, { active: templateVersionNumber(active.version) })
}

// Mirrors the server's brand set (src/server/models/brands.ts): the key, the name from the texts.
export const BRANDS = CLIENT_BRAND_KEYS.map((key) => ({ key, name: textOf(CLIENT_BRAND_NAMES, key) }))

export const brandName = (key: string) => BRANDS.find((b) => b.key === key)?.name ?? key

// Option text of a label sheet and of an envelope format, built from its own data.
export const labelPresetText = (p: { key: string, widthMm: number, heightMm: number, columns: number, rows: number }) =>
    fill(LABEL_PRESET_OPTION, { name: textOf(LABEL_PRESET_NAMES, p.key), w: p.widthMm, h: p.heightMm, count: p.columns * p.rows })
export const envelopeText = (key: string, size: { w: number, h: number }) =>
    fill(ENVELOPE_OPTION, { name: textOf(ENVELOPE_NAMES, key), w: size.w, h: size.h })

// EU member states (ISO2 + German name). Lived inside VatSettings.tsx until 2026-09-08 and
// was lifted here when the author mask needed the same thing: one list, not a second one
// beside it. The VAT RATES stay on the server (single source of truth via /settings/vat-status)
// — only the names are here.
//
// EU only, and that is a decision, not an omission: an author is settled under reverse charge
// from Cyprus, which requires an EU VAT id. Somebody outside the EU cannot be settled at all,
// so offering them in the picker would promise something the settlement run then refuses.
// The codes only; the German names come from the browser, like ALL_COUNTRIES below. Kept in
// code order, as the list stood before.
const EU_COUNTRY_CODES = 'AT BE BG CY CZ DE DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK'.split(' ')
const euCountryNames = new Intl.DisplayNames(LANGUAGE, { type: 'region' })
export const EU_COUNTRIES: { code: string; name: string }[] =
    EU_COUNTRY_CODES.map((code) => ({ code, name: euCountryNames.of(code) ?? code }))

// ALL countries — for customers, who may sit anywhere (Switzerland being the usual case), unlike
// authors (EU only, see above). The codes are ISO 3166-1 alpha-2, a standard, not a choice of
// ours; the German names come from the browser (Intl.DisplayNames) instead of a second
// hand-kept name list. Sorted by name. The codes live in shared/countries.ts — the server checks
// against the same list.
export const ALL_COUNTRIES: { code: string; name: string }[] = (() => {
    const names = new Intl.DisplayNames(LANGUAGE, { type: 'region' })
    return ISO_COUNTRY_CODES
        .map((code) => ({ code, name: names.of(code) ?? code }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de'))
})()

// The backoffice currency (Einstellungen → Allgemein), set from the session (AuthContext) — the
// fallback for every amount that carries no currency of its own.
let appCurrency = DEFAULT_CURRENCY
export const setAppCurrency = (code?: string) => { if (code) appCurrency = code }

// An amount with its currency, the way the lists show money: the record's own currency where it
// has one, else the backoffice currency. The ONE formatter — the lists used to carry copies.
// The sign for labels like „Festbetrag (€)": of the given currency, else of the backoffice one.
export const moneySign = (currency?: string) => currencySymbol(currency || appCurrency)

export const formatMoney = (n: number, currency?: string, opts: { whole?: boolean } = {}) =>
    new Intl.NumberFormat('de-DE', {
        style: 'currency', currency: currency || appCurrency, ...(opts.whole ? { maximumFractionDigits: 0 } : {}),
    }).format(n)

// Written-out name for a country code. Falls back to the CODE itself, on purpose: a record
// carrying something the list does not know (an old entry, a non-EU country) must still show
// what is actually stored instead of an empty cell.
export const countryName = (code?: string) => {
    const c = (code ?? '').trim().toUpperCase()
    if (!c) return ''
    return EU_COUNTRIES.find((x) => x.code === c)?.name ?? c
}

// The month folder itself as a `folder` value (files directly in it, e.g. report.pdf) — server: ROOT_FOLDER.
export const MONTH_ROOT_FOLDER = 'root'
